import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { useAuth } from '../contexts/AuthContext';
import { toast } from '../hooks/use-toast';
import { Lock, Mail, Loader2, RefreshCw } from 'lucide-react';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [loginAttempts, setLoginAttempts] = useState(0);
  const { login } = useAuth();
  const navigate = useNavigate();

  // Only clear stale auth data if there is no valid token
  // (prevents wiping token during auth-loading redirect race condition)
  useEffect(() => {
    const existingToken = localStorage.getItem('token');
    if (!existingToken) {
      localStorage.removeItem('user');
      localStorage.removeItem('facility');
    }
  }, []);

  const handleClearAndRetry = () => {
    // Clear ALL app data
    localStorage.clear();
    sessionStorage.clear();
    
    // Clear service worker caches
    if ('caches' in window) {
      caches.keys().then(names => {
        names.forEach(name => caches.delete(name));
      });
    }
    
    toast({
      title: "Cache Cleared",
      description: "App data cleared. Please try logging in again.",
    });
    
    setLoginAttempts(0);
    setEmail('');
    setPassword('');
    
    // Reload the page to ensure clean state
    window.location.reload();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setLoginAttempts(prev => prev + 1);
    
    try {
      const result = await login(email, password);
      
      if (result.success) {
        toast({
          title: "Login Successful",
          description: `Welcome back, ${result.user.name}!`,
        });

        // Runbook A (dormant): if this account is on a temporary password, force
        // a password change before anything else. No account has this flag until
        // the coordinated rotation, so normal logins are unaffected.
        if (result.must_change_password) {
          navigate('/admin/change-password');
          return;
        }

        // Redirect based on role
        if (result.user.role === 'superadmin' || result.user.is_super_admin) {
          navigate('/superadmin');
        } else if (result.user.role === 'venue_manager') {
          navigate('/admin'); // Venue managers go to admin but with read-only access
        } else {
          navigate('/admin');
        }
      } else {
        toast({
          title: "Login Failed",
          description: result.error,
          variant: "destructive"
        });
      }
    } catch (error) {
      toast({
        title: "Login Failed",
        description: error.message || "An error occurred",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-emerald-50 px-4">
      <Card className="w-full max-w-md border-gray-200 shadow-lg">
        <CardHeader className="text-center">
          <img 
            src="https://customer-assets.emergentagent.com/job_fieldmaster-24/artifacts/iuxgdyho_Spaces-Logo.png" 
            alt="Spaces by TRP Sports" 
            className="h-16 mx-auto mb-4"
          />
          <CardTitle className="text-2xl text-gray-800">Admin Login</CardTitle>
          <CardDescription>Sign in to manage your facility</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="email">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                <Input
                  id="email"
                  data-testid="admin-login-email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="admin@facility.com"
                  className="pl-10"
                  disabled={loading}
                />
              </div>
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                <Input
                  id="password"
                  data-testid="admin-login-password"
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="pl-10"
                  disabled={loading}
                />
              </div>
            </div>
            <Button type="submit" data-testid="admin-login-submit" className="w-full bg-blue-600 hover:bg-blue-700" disabled={loading}>
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Signing in...
                </>
              ) : (
                'Sign In'
              )}
            </Button>
          </form>
          
          {/* Show troubleshooting option after 2 failed attempts */}
          {loginAttempts >= 2 && (
            <div className="mt-4 pt-4 border-t">
              <p className="text-sm text-gray-500 text-center mb-2">
                Having trouble logging in?
              </p>
              <Button 
                type="button" 
                variant="outline" 
                className="w-full text-gray-600"
                onClick={handleClearAndRetry}
              >
                <RefreshCw className="mr-2 h-4 w-4" />
                Clear Cache & Retry
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Login;
